// frontend/src/components/AuthLayout.js

import React, { useEffect } from "react";
import { Box } from "@mui/material";
import Navbar from "./Navbar";
import HomeNav from "./homepage/HomeNav";
import Footer from "./Footer";
import HomeFooter from "./homepage/HomeFooter";
import SkipToContent from "./SkipToContent";
import ConversionPopupGate from "./conversion/ConversionPopupGate";
import { GuideProvider } from "../contexts/GuideContext";
import { colors } from "../theme/tokens";
import useSiteIsHebrew from "../hooks/useSiteIsHebrew";
import { releaseAuthBoot } from "../utils/authBoot";

// navVariant: "app" renders the production Navbar and Footer; "home" renders
// the public site's floating pill nav and its own footer (components/homepage/
// HomeNav.js + HomeFooter.js) instead. A PUBLIC page (`isPublic`) always gets
// the pill (owner round 2: homepage, blog, the landings, the legal pages for a
// signed-out visitor) - even if it passes navVariant="app", because the app
// Navbar has no logged-out shape left (#442). On a signed-in page navVariant
// may still pick either bar; it defaults to the app chrome. HomeNav renders
// its own top spacer off the homepage, so no page file needs to change.
// Nothing else in the layout changes with it.
function AuthLayout({ children, isPublic = false, navVariant }) {
  const variant = isPublic ? "home" : navVariant || "app";
  // The one site-language rule (utils/siteLanguage.js): Hebrew unless the
  // visitor explicitly chose English. Hebrew on the hydration pass of a
  // prerendered snapshot (the public pages are prerendered in Hebrew), the
  // stored preference from the next render on - and from the very first
  // render of a client-only boot (a route change), where it used to arrive one
  // effect late, after a Hebrew frame. hooks/useSiteIsHebrew.js (#464).
  const [isHebrew, setSiteIsHebrew] = useSiteIsHebrew();

  const toggleLanguage = () => setSiteIsHebrew(!isHebrew);

  // Take the authenticated-boot splash down (public/index.html, backlog #158):
  // this layout's first commit is the first frame of a real page. Most
  // AuthLayout routes (the Dashboard, ...) sit inside a <Suspense> ABOVE the
  // layout (ProtectedRoute's cold-boot boundary, or App's), so while the page
  // chunk is still loading the layout is not committed at all and this effect
  // has not run - the splash stays up over the auth gate's and the route's
  // loaders instead of uncovering them. Exception: /settings, /admin and /admin/users/:userId
  // put the Suspense INSIDE the layout (loading/PageBodySuspense.js), so the
  // layout commits first and the splash comes down onto navbar + page
  // skeleton - intended. A no-op on every anonymous load and every later
  // mount (utils/authBoot.js).
  useEffect(() => {
    releaseAuthBoot();
  }, []);

  return (
    <GuideProvider isHebrew={isHebrew}>
    <Box
      dir={isHebrew ? "rtl" : "ltr"}
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
      role="document"
    >
      <SkipToContent />
      {/* Background - plain white for public homepage, animated orbs for auth pages */}
      {!isPublic && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
            pointerEvents: "none",
          }}
        >
          {/* Background gradient */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(150deg, #EDFAF8 0%, #F8FDFF 50%, #FDF8F9 100%)",
            }}
          />

          {/* Animated gradient orbs. No inline `animation`: the .gradient-orb
              class carries it, gated in index.css to desktop widths without
              prefers-reduced-motion (an inline shorthand here would bypass
              that gate and keep 4 permanently re-blurring layers running on
              mobile). The sx animationDelay longhand still wins over the
              class shorthand, so the desktop stagger is unchanged. */}
          <Box
            className="gradient-orb"
            sx={{
              position: "absolute",
              width: "600px",
              height: "600px",
              borderRadius: "50%",
              background:
                `radial-gradient(circle, ${colors.primary.alpha30} 0%, transparent 70%)`,
              filter: "blur(80px)",
              top: "-200px",
              left: "-200px",
              animationDelay: "0s",
            }}
          />
          <Box
            className="gradient-orb"
            sx={{
              position: "absolute",
              width: "800px",
              height: "800px",
              borderRadius: "50%",
              background:
                `radial-gradient(circle, ${colors.secondary.alpha30} 0%, transparent 70%)`,
              filter: "blur(80px)",
              bottom: "-300px",
              right: "-300px",
              animationDelay: "5s",
            }}
          />
          <Box
            className="gradient-orb"
            sx={{
              position: "absolute",
              width: "500px",
              height: "500px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255, 182, 193, 0.3) 0%, transparent 70%)",
              filter: "blur(80px)",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              animationDelay: "10s",
            }}
          />
          <Box
            className="gradient-orb"
            sx={{
              position: "absolute",
              width: "400px",
              height: "400px",
              borderRadius: "50%",
              background:
                `linear-gradient(135deg, rgba(255, 182, 193, 0.3) 0%, ${colors.primary.alpha30} 100%)`,
              filter: "blur(80px)",
              bottom: "100px",
              left: "-100px",
              animationDelay: "15s",
            }}
          />
        </Box>
      )}

      {/* Navigation */}
      {variant === "home" ? (
        <HomeNav isHebrew={isHebrew} toggleLanguage={toggleLanguage} />
      ) : (
        <Navbar isHebrew={isHebrew} toggleLanguage={toggleLanguage} />
      )}

      {/* Main Content */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        role="main"
        aria-label="Main content"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 1,
          outline: "none",
        }}
      >
        {React.cloneElement(children, { isHebrew, toggleLanguage })}
      </Box>

      {/* Footer */}
      {variant === "home" ? <HomeFooter isHebrew={isHebrew} /> : <Footer isHebrew={isHebrew} />}

      {/* The conversion popup - public pages only; the gate decides the rest
          (route allowlist, signed out, organic, Hebrew, consent, timing). */}
      {isPublic && <ConversionPopupGate isHebrew={isHebrew} />}

      {/* The orbs' `float` keyframes live in index.css (static, gated there
          to desktop widths without reduced motion). This layout used to
          re-declare an identical copy in an inline <style>; keyframes belong
          in static stylesheets only, so the copy is gone. */}
    </Box>
    </GuideProvider>
  );
}

export default AuthLayout;
